// apps/web/src/features/admin/lib/link-target-path.ts
//
// Shared by JobVersionWorkspace.handleErrorClick (publish-error deep links,
// R-71) and ImportFromGithubSection's resolution-report finding clicks
// (S-6 Task 8 controller ruling, 2026-08-09) — both turn a
// ValidationError-shaped `linkTarget` into a navigation + optional
// scroll-into-view. Extracted so the two call sites can't drift on which
// tabs live on which admin surface.
//
// 'header'/'pipeline' (and any future job-config-level tab) live on the Job
// Config surface (`/admin/job-types/:id`); every other tab — fields,
// export-mapping, zones, history, sample, ... — lives on the Form Config
// surface (`/admin/forms/:id`). See JobVersionWorkspace.tsx's original
// comment for why: Header + Pipeline moved to Job Config on 2026-06-28.
import type { NavigateFunction } from 'react-router';
import { scrollFieldIntoView } from '../form-config/fields/scrollFieldIntoView';

const JOB_CONFIG_TABS = new Set(['header', 'pipeline']);

export interface LinkTarget {
  tab: string;
  fieldName?: string;
}

/** Pure: which admin surface + tab a linkTarget resolves to. */
export function buildLinkTargetPath(jobTypeId: string, target: LinkTarget): string {
  const base = JOB_CONFIG_TABS.has(target.tab)
    ? `/admin/job-types/${jobTypeId}`
    : `/admin/forms/${jobTypeId}`;
  return `${base}?tab=${target.tab}`;
}

/** Navigates to the linkTarget's surface, then (if the target names a
 *  field) scrolls it into view once the destination tab's rows have
 *  mounted — a rAF tick after navigate, mirroring R-71's original timing. */
export function navigateToLinkTarget(navigate: NavigateFunction, jobTypeId: string, target: LinkTarget): void {
  navigate(buildLinkTargetPath(jobTypeId, target));
  if (target.fieldName) {
    const fieldName = target.fieldName;
    requestAnimationFrame(() => {
      scrollFieldIntoView(fieldName);
    });
  }
}
